<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import lodash, { cloneDeep } from 'lodash-es'
import { Search } from '@element-plus/icons-vue'
import { ElMessageBox } from 'element-plus'
import { copyText } from '@user-center/utils/common'
import { $t } from "@user-center/utils/i18n.ts";

const props = defineProps({
  title: {
    type: String,
    default: $t('text.batchSearch'),
  },
  subPlaceholder: {
    type: String,
    default: $t('placeholder.batchSearchSplitTip'),
  },
  prop: {
    type: String,
    default: '',
  },
  tipUnFindSearchData: {
    type: Boolean,
    default: true,
  },
  sortOrder: {
    type: Boolean,
    default: false,
  },
})

const modelValue = defineModel()
// sourceText 换行 \n 符分割
const sourceText = ref('')
// sourceInputText 逗号 , 分隔
const sourceInputText = ref('')
const options = ref<string[]>([])

watch(modelValue, (value) => {
  if (value && Array.isArray(value)) {
    sourceText.value = value.join('\n')
    sourceInputText.value = value.join(',')
  } else {
    sourceText.value = ''
    sourceInputText.value = ''
  }
}, {
  immediate: true,
  deep: true,
})

watch(sourceText, (value) => {
  const result = value.split(/[\s+,，]/g).filter((item: any) => { return item })
  options.value = result.map(item => item.trim())
}, {
  immediate: true,
})

watch(sourceInputText, (value: string) => {
  // 逗号替换成 换行
  sourceText.value = value.replace(/,/g, '\n')
}, {
  immediate: true,
})

function onBlur() {
  if (sourceText.value) {
    const dataList: string[] = lodash.uniq(
      sourceText.value.split(/[\s,，;]/).filter((o: any) => !!o),
    )
    sourceText.value = dataList.join('\n')
    sourceInputText.value = dataList.join(',')
  }
}

function onInputBlur() {
  onBlur()
  onConfirm()
}

const emit = defineEmits(['enter'])
function onEnter() {
  onBlur()
  onConfirm()
  nextTick(() => {
    emit('enter')
  })
}

const inputRef = ref()

const dialogVisible = ref(false)
function changeVisible() {
  dialogVisible.value = !dialogVisible.value
  nextTick(() => {
    dialogVisible.value && inputRef.value?.focus()
  })
}

function onConfirm() {
  modelValue.value = cloneDeep(options.value)
  dialogVisible.value = false
}

function tipNotFindResultData(data: any[], prop: string = '', tip =$t('placeholder.batchSearchUnFindDataInCurrentPage'),) {
  prop = prop || props.prop
  if (!prop) {
    return
  }
  const dataList = data.map(item => String(item[prop] || '')).filter(item => item)
  const dataSet = new Set(dataList)
  const notFindResultData = options.value.filter(item => !dataSet.has(item))
  if (notFindResultData.length) {
    copyText(
      notFindResultData.map((o: any): any => o || '').join('\n'),
      $t('messages.copyUnFindData'),
    )
    ElMessageBox.alert(
      `<textarea style="width:100%;height:100px;border:1px solid #eee;">${notFindResultData.map((o: any): any => o || '').join('\n')}</textarea>`,
      tip,
      {
        dangerouslyUseHTMLString: true,
      },
    )
    return false
  }

  return true
}

function filterResultData(data: any[], prop: string = '', tip = $t('placeholder.batchSearchUnFindDataInCurrentPage')) {
  prop = prop || props.prop
  if (props.tipUnFindSearchData) {
    tipNotFindResultData(data, prop, tip)
  }

  if (props.sortOrder) {
    // 根据批量搜索顺序排序, 未匹配到的数据则排到最后
    return lodash.sortBy(data, (item) => {
      const index = options.value.indexOf(String(item[prop]))
      return index === -1 ? Infinity : index
    })
  }

  return data
}

defineExpose({
  tipNotFindResultData,
  filterResultData,
})
</script>

<template>
  <el-input
    v-bind="$attrs"
    v-model="sourceInputText"
    @blur="onInputBlur"
    @keyup.enter="onEnter"
  >
    <template #append>
      <el-button :icon="Search" @click="changeVisible" />
    </template>
  </el-input>

  <el-dialog v-model="dialogVisible" :title="title" append-to-body width="45%">
    <el-input
      v-if="dialogVisible" ref="inputRef" v-model="sourceText" autofocus :autosize="{ minRows: 10, maxRows: 20 }"
      type="textarea" :placeholder="subPlaceholder" @blur="onBlur"
    />
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="dialogVisible = false">{{$t('actions.取消')}}</el-button>
        <el-button type="primary" @click="onConfirm">{{$t('actions.确认')}} </el-button>
      </span>
    </template>
  </el-dialog>
</template>

<style scoped>
:deep(.el-message-box__message) {
  width: 100%;
}
</style>
